import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MapPin } from "@/lib/public-icons";
import { useMemo, useState } from "react";
import { optionalString } from "@/lib/search";
import { Skeleton } from "@/components/ui/skeleton";
import { formatEmploymentType } from "@/lib/words/employment-type";
import { orpcPublic } from "@/lib/orpc";
import { FacilityCareersAssistant } from "@/routes/careers/-components/careers-assistant";
import { filterChoices } from "@/routes/careers/-components/filter-choices";
import { JobFilterSelect } from "@/routes/careers/-components/job-filter-select";
import { JobListingCard } from "@/routes/careers/-components/job-listing-card";
import { CareersHero } from "@/routes/careers/$slug/-components/careers-hero";
import { CareersNotFound } from "@/routes/careers/$slug/-components/careers-not-found";
import { CareersShell } from "./-components/careers-shell";
import { SearchField } from "@/components/app/patterns/search-field";

export const Route = createFileRoute("/careers/$slug/")({
  /* `?board=` carries the board's public slug, the object the API validates. An unusable value is
   * dropped, so a hand-built empty `?board=` shows the whole building, not an error page. */
  validateSearch: (search: Record<string, unknown>): { board?: string } => {
    const board = optionalString(search.board)?.trim();
    return { board: board && board.length <= 120 ? board : undefined };
  },
  component: CareersPage,
});

/** One building's open positions, as a candidate sees them. */
function CareersPage() {
  const { slug } = Route.useParams();
  const { board } = Route.useSearch();
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("all");
  const [location, setLocation] = useState("all");
  const [employment, setEmployment] = useState("all");

  const page = useQuery({
    ...orpcPublic.careers.facility.queryOptions({ input: { careersSlug: slug, board } }),
    retry: false,
  });

  const jobs = useMemo(() => page.data?.jobs ?? [], [page.data?.jobs]);
  const departments = useMemo(() => filterChoices(jobs.map((job) => job.department)), [jobs]);
  // A single building can still post across sites: a community with a memory
  // care wing, or a role covering two addresses.
  const locations = useMemo(() => filterChoices(jobs.map((job) => job.location)), [jobs]);
  const employmentTypes = useMemo(
    () => filterChoices(jobs.map((job) => job.employmentType)),
    [jobs],
  );

  const shown = useMemo(() => {
    const query = search.trim().toLowerCase();
    return jobs.filter((job) => {
      if (department !== "all" && job.department !== department) return false;
      if (location !== "all" && job.location !== location) return false;
      if (employment !== "all" && job.employmentType !== employment) return false;
      if (!query) return true;
      return [job.title, job.department, job.location].some((field) =>
        field?.toLowerCase().includes(query),
      );
    });
  }, [jobs, search, department, location, employment]);

  if (page.isError) {
    return (
      <CareersShell slug={slug}>
        <CareersNotFound />
      </CareersShell>
    );
  }

  const facilityName = page.data?.facility.name;
  const where = [page.data?.facility.city, page.data?.facility.state].filter(Boolean).join(", ");
  const brand = page.data?.organization;
  // Named only when the link asked for a board, and named by the server: two
  // boards at one building are otherwise the same page twice over.
  const boardName = page.data?.board?.name;

  return (
    <CareersShell facilityName={facilityName} slug={slug}>
      <CareersHero
        accentColor={brand?.accentColor}
        headline={brand?.headline}
        tagline={brand?.tagline}
      />

      <div className="mb-8">
        {page.isPending ? (
          <Skeleton className="h-10 w-72" />
        ) : (
          <h1 className="text-3xl font-semibold tracking-tight text-balance">
            {facilityName ? (
              <>
                Careers at <span className="inline-block">{facilityName}</span>
              </>
            ) : (
              "Open positions"
            )}
          </h1>
        )}
        {where ? (
          <p className="text-muted-foreground mt-1 flex items-center gap-1">
            <MapPin className="size-4" />
            {where}
          </p>
        ) : null}
        <p className="text-muted-foreground mt-3 max-w-2xl">
          {/* A board name is a whole phrase ("Open positions at Main"); set
              into a sentence it read "Openings on the Open positions… board". */}
          {boardName ? (
            <>
              Showing {boardName}.{" "}
              <Link
                className="text-primary-ink underline underline-offset-4"
                params={{ slug }}
                to="/careers/$slug"
              >
                See every opening
              </Link>
            </>
          ) : (
            "Browse our current openings and apply online."
          )}
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        <SearchField
          placeholder="Search positions…"
          value={search}
          onChange={setSearch}
          className="min-w-60 flex-1"
        />
        {/* Each filter appears only when the postings actually differ, so a
            building with one address gets no useless control. */}
        <JobFilterSelect
          label="Filter by department"
          allLabel="All departments"
          values={departments}
          value={department}
          onChange={setDepartment}
        />
        <JobFilterSelect
          label="Filter by location"
          allLabel="All locations"
          values={locations}
          value={location}
          onChange={setLocation}
        />
        <JobFilterSelect
          label="Filter by employment type"
          allLabel="All types"
          values={employmentTypes}
          value={employment}
          onChange={setEmployment}
          format={formatEmploymentType}
        />
      </div>

      {page.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : shown.length === 0 ? (
        <div className="text-muted-foreground py-12 text-center">
          {jobs.length > 0
            ? "No positions match your search."
            : boardName
              ? `No open positions on the ${boardName} board at this time.`
              : "No open positions at this time."}
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((job) => (
            <JobListingCard key={job.id} job={job} careersSlug={slug} heading="h3" />
          ))}
        </div>
      )}

      {/* After the postings, because somebody who found the job they wanted
          has no question to ask. */}
      <div className="mt-10">
        <FacilityCareersAssistant careersSlug={slug} />
      </div>
    </CareersShell>
  );
}
